import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { fromJS } from 'immutable';
import { useMemo, useRef, useState, UIEvent } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useVirtual } from 'react-virtual';

// ASSETS
import MoreIcon from 'assets/MoreIcon';

// CONSTANTS
import { DevicelessTableTab } from 'constants/telematics';

// COMPONENTS
import { SecondaryButton } from 'components/Buttons';
import Ellipsing from 'components/Ellipsing/Ellipsing.component';
import { CLASSES as TOOLTIP_CLASSES } from 'components/Tooltip';
import { CLASSES } from 'components/DropdownMenu';
import { FailedTag, InQueueTag, PartiallySucceededTag, SucceededTag, UnEnrollmentPendingTag } from './tags';

// SERVICES
import { Enrolment, EnrolmentStatus } from 'services/resources';
import { selectEnrolments } from 'services/Telematics/selectors';

// HOOKS
import { useUnenrollment } from './Unenrollment.context';

// STYLES
import colors from 'styles/colors';
import { LinkContainer } from '../../styles';
import { StyledDropdownMenu } from '../../../styles';

enum EnrolmentActions {
  UNENROL,
}

type originalExtraAction = {
  id: EnrolmentActions;
  renderedValue: { translationId: string } | string;
  handleClick: (row: Enrolment, event: UIEvent) => void;
};

type mappedExtraAction = {
  id: EnrolmentActions;
  renderedValue: { translationId: string } | string;
  handleClick: (item: originalExtraAction, event: UIEvent) => void;
};

const removeAccents = (str: string) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const getStatusTag = (status: EnrolmentStatus) => {
  switch (status) {
    case EnrolmentStatus.SUCCEEDED:
      return <SucceededTag />;
    case EnrolmentStatus.FAILED:
      return <FailedTag />;
    case EnrolmentStatus.INQUEUE:
      return <InQueueTag />;
    case EnrolmentStatus.PARTIALLY_SUCCEEDED:
      return <PartiallySucceededTag />;
    case EnrolmentStatus.UNENROLLMENT_PENDING:
      return <UnEnrollmentPendingTag />;
    default:
      return 'Unknown';
  }
};

const TAB_STATUSES: Partial<Record<DevicelessTableTab, EnrolmentStatus[]>> = {
  [DevicelessTableTab.Active]: [EnrolmentStatus.SUCCEEDED, EnrolmentStatus.PARTIALLY_SUCCEEDED],
  [DevicelessTableTab.Pending]: [EnrolmentStatus.INQUEUE, EnrolmentStatus.UNENROLLMENT_PENDING],
  [DevicelessTableTab.Failed]: [EnrolmentStatus.FAILED, EnrolmentStatus.UNKNOWN],
};

const useDevicelessTable = () => {
  const intl = useIntl();

  const { values: enrolments, loading } = useSelector(selectEnrolments());

  const columnHelper = useMemo(() => createColumnHelper<Enrolment>(), []);
  const { openModal: openUnenrollmentModal, setEnrolment } = useUnenrollment();

  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [activeTab, setActiveTab] = useState<DevicelessTableTab>(DevicelessTableTab.Active);

  const filteredData = useMemo(
    () => enrolments.filter((enrolment) => TAB_STATUSES[activeTab]?.includes(enrolment.status)),
    [enrolments, activeTab]
  );

  const activeCount = useMemo(
    () => enrolments.filter((enrolment) => TAB_STATUSES[DevicelessTableTab.Active]?.includes(enrolment.status)).length,
    [enrolments]
  );
  const pendingCount = useMemo(
    () => enrolments.filter((enrolment) => TAB_STATUSES[DevicelessTableTab.Pending]?.includes(enrolment.status)).length,
    [enrolments]
  );
  const failedCount = useMemo(
    () => enrolments.filter((enrolment) => TAB_STATUSES[DevicelessTableTab.Failed]?.includes(enrolment.status)).length,
    [enrolments]
  );

  const handleSelectedTab = (selectedTab: DevicelessTableTab) => {
    if (selectedTab === DevicelessTableTab.Active && activeTab !== DevicelessTableTab.Active) {
      setActiveTab(selectedTab);
    }
    if (selectedTab === DevicelessTableTab.Pending && activeTab !== DevicelessTableTab.Pending && pendingCount > 0) {
      setActiveTab(selectedTab);
    }
    if (selectedTab === DevicelessTableTab.Failed && activeTab !== DevicelessTableTab.Failed && failedCount > 0) {
      setActiveTab(selectedTab);
    }
  };

  const customGlobalFilter = (row, columnId, filterValue) => {
    const searchText = filterValue.toLowerCase();

    // For the status columm, perform a custom search based on the latest vehicle's connected status.
    if (columnId === 'status') {
      const status = row.original.status;
      const statusTranslations = intl.formatMessage({
        id: `views.FleetSettings.OEMTelematics.Status.${status}`,
      });

      const statusText = removeAccents(statusTranslations);
      return statusText.toLowerCase().includes(searchText);
    }

    // For other columns, perform a default string match on the raw value.
    const value = row.getValue(columnId);
    return String(value).toLowerCase().includes(searchText);
  };

  const customColumnCanGlobalFilter = (column) => {
    const telematicsFilterableFields = ['vehicle_vin', 'status'];
    return telematicsFilterableFields.includes(column.id);
  };

  const columns = useMemo(() => {
    const mapHandleClickToRow = (row: Enrolment, extraAction: originalExtraAction): mappedExtraAction => {
      const newClickHandler = (_item: originalExtraAction, event: UIEvent) => {
        extraAction.handleClick(row, event);
      };

      return { ...extraAction, handleClick: newClickHandler };
    };

    const extraActions: {
      id: EnrolmentActions;
      renderedValue: { translationId: string } | string;
      handleClick: (row: Enrolment, event: UIEvent) => void;
    }[] = [
      {
        id: EnrolmentActions.UNENROL,
        renderedValue: { translationId: 'views.FleetSettings.OEMTelematics.unenrollAction' },
        handleClick: async (row: Enrolment, event) => {
          await setEnrolment(row);
          openUnenrollmentModal(event);
        },
      },
    ];

    const actions: React.JSXElementConstructor<{ row: Enrolment }>[] = [];

    const columns = [
      columnHelper.accessor('status', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.OEMTelematics.Table.Headers.status',
        }),
        size: 100,
        cell: (field) => {
          const status = field.getValue();
          const statusTranslations = getStatusTag(status);

          return statusTranslations;
        },
      }),
      columnHelper.accessor('vehicle.vin', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.OEMTelematics.Table.Headers.VIN',
        }),
        size: 100,
        cell: (field) => {
          const vin = field.getValue();

          const vehicleId = field.row.original.vehicle.id;

          return (
            <LinkContainer>
              <Link to={`/fleet/vehicles/${vehicleId}`}>
                <Ellipsing text={vin} />
              </Link>
            </LinkContainer>
          );
        },
      }),
    ];

    if (actions.length > 0 || extraActions.length > 0) {
      columns.push(
        columnHelper.display({
          id: 'actions',
          header: '',
          size: 100,
          cell: (props) => {
            const status = props.row.original.status;

            const availableExtraActions = extraActions.filter((action) => {
              return (
                action.id !== EnrolmentActions.UNENROL ||
                (action.id === EnrolmentActions.UNENROL &&
                  [EnrolmentStatus.SUCCEEDED, EnrolmentStatus.PARTIALLY_SUCCEEDED].includes(status))
              );
            });

            return (
              <div style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
                {extraActions.length > 0 && (
                  <StyledDropdownMenu
                    items={fromJS(
                      availableExtraActions.map((extraAction) => mapHandleClickToRow(props.row.original, extraAction))
                    )}
                    buttonComponent={SecondaryButton}
                    buttonChildren={null}
                    className={`${CLASSES.NO_RIGHT_PADDING} ${TOOLTIP_CLASSES.ALIGN_RIGHT}`}
                    icon={<MoreIcon color={colors.blue} />}
                    iconOnly
                  />
                )}
              </div>
            );
          },
        })
      );
    }

    return columns;
  }, [columnHelper, intl, openUnenrollmentModal, setEnrolment]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: customGlobalFilter,
    getColumnCanGlobalFilter: customColumnCanGlobalFilter,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const tableContainerRef = useRef(null);

  const { rows } = table.getRowModel();
  const {
    virtualItems: virtualRows,
    totalSize,
    scrollToIndex,
  } = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 5,
  });

  return {
    rows,
    dataLoading: loading,
    virtualRows,
    tableContainerRef,
    totalSize,
    table,
    scrollToIndex,
    setGlobalFilter,
    globalFilter,
    activeTab,
    handleSelectedTab,
    activeCount,
    pendingCount,
    failedCount,
  };
};

export default useDevicelessTable;
